import useEventEmitter from '@/_core/hooks/useEventEmitter'
import useApiRequest from '@/_core/hooks/useApiRequest'
import { getThemes, sendForm } from '@/services/supportForm'
import { reactive, ref, inject, computed } from 'vue'
import { megabitToByte } from '@/_core/helpers/converters'
import { email, maxLength, minLength, required } from '@vuelidate/validators'
import useVuelidate from '@vuelidate/core'

export default function SupportFormController () {
  const { $on, $off, $emit } = useEventEmitter()

  // consts
  const store = inject('store')
  const isFormSent = ref(false)
  const fileMaxSize = 5
  const filesMaxLength = 5
  const messageLength = 20

  // themes
  const themes = ref([])
  const themesRequest = useApiRequest(getThemes)
  themesRequest.$on('success', () => {
    $emit('requestThemes-success', themesRequest.data)
  })
  themesRequest.$on('error', () => {
    $emit('requestThemes-error', themesRequest.errors)
  })
  const requestThemes = async () => {
    await themesRequest.request()
  }
  $on('requestThemes-success', data => {
    themes.value = data.map(el => ({
      value: el.id,
      label: el.theme
    }))
  })

  // send form
  const sendRequest = useApiRequest(sendForm)
  sendRequest.$on('success', () => {
    $emit('requestSend-success', sendRequest.data)
  })
  sendRequest.$on('error', () => {
    $emit('requestSend-error', sendRequest.errors)
  })
  const requestSend = async form => {
    const data = new FormData()
    data.append('email', form.email)
    data.append('theme', form.theme)
    data.append('message', form.message)
    form.files.forEach((file, index) => {
      data.append(`file${index + 1}`, file, file.name)
    })
    await sendRequest.request(data)
  }
  $on('requestSend-success', () => {
    isFormSent.value = true
    v$.value.$reset()
  })
  $on('requestSend-error', () => {
    alert('Произошла ошибка. Попробуйте позже.')
  })

  // form
  const form = reactive({
    email: store.user.profile?.email,
    theme: null,
    message: '',
    files: []
  })

  // rules
  const fileSizes = files => {
    if (files) return files.every(file => file.size <= megabitToByte(fileMaxSize))
    return true
  }
  const rules = {
    email: { required, email },
    theme: { required },
    message: {
      required,
      minLength: minLength(messageLength)
    },
    files: {
      fileSizes,
      maxLength: maxLength(filesMaxLength)
    }
  }
  const v$ = useVuelidate(rules, form, { $lazy: true })

  // errors
  const errors = {
    email: {
      required: 'Поле обязательно для заполнения',
      email: 'Не верный формат почты'
    },
    theme: {
      required: 'Поле обязательно для заполнения'
    },
    message: {
      required: 'Обязательно для заполнения',
      minLength: `Не достаточное количество символов, минимум ${messageLength}`
    },
    files: {
      maxLength: 'Превышено количество файлов',
      fileSize: 'Превышен допустимый размер файла'
    }
  }

  const updateFiles = data => {
    form.files = data
    v$.value.files.$touch()
  }

  const onSubmit = () => {
    v$.value.$touch()
    if (v$.value.$invalid || !v$.value.$anyDirty) return
    requestSend(form)
  }

  return {
    requestThemes,
    $on,
    $off,
    isFormSent,
    fileMaxSize,
    filesMaxLength,
    themes,
    form,
    v$,
    errors,
    updateFiles,
    onSubmit,
    sendRequestIsLoading: computed(() => sendRequest.isLoading)
  }
}
